import { motion, useInView } from 'framer-motion';
import { Button, useInViewScroll } from 'ui';
import { XumoForm } from './Form';
import { InputType } from '../types';
import { useSanitizedTranslation } from '../hooks';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import * as yup from 'yup';

interface SweepstakeProps {
  headingBold?: string;
  heading?: string;
  body?: string;
  successTitle?: string;
  successMessage?: string;
  errorMessage?: string;
  ctaLabel?: string;
  id?: string;
  hideGradient?: boolean;
  verticalStack?: boolean;
  startDate?: string | Date;
  endDate?: string | Date;
}

export const Sweepstake = ({
  headingBold,
  heading,
  body,
  successTitle,
  successMessage,
  errorMessage,
  ctaLabel,
  id = 'test-sweepstake-id',
  hideGradient = false,
  verticalStack = true,
  startDate = '2025-06-01T05:00:00Z', // Midnight EST (UTC-5)
  endDate = '2025-06-18T04:59:59Z', // Midnight EST (UTC-5)
}: SweepstakeProps) => {
  const { t } = useSanitizedTranslation();
  const router = useRouter();
  const slugifiedPath =
    id ??
    router.asPath
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Add useEffect to check the marketing checkbox on component load
  useEffect(() => {
    setTimeout(() => {
      const marketingCheckbox = document.querySelector('input[name="marketing"]') as HTMLInputElement;
      if (marketingCheckbox) {
        // Trigger a click event instead of directly setting the checked property
        if (!marketingCheckbox.checked) {
          marketingCheckbox.click();
        }
      }
    }, 100); // Short delay to ensure the checkbox is rendered
  }, []);

  const { ref, progress: amt } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });
  const currentDate = new Date();
  const sweepstakeEnded = currentDate < new Date(startDate) || currentDate > new Date(endDate);
  console.table({
    currentDate: currentDate.toISOString(),
    startDate: new Date(startDate).toISOString(),
    endDate: new Date(endDate).toISOString(),
    sweepstakeEnded: sweepstakeEnded,
    id: slugifiedPath,
  });
  if (sweepstakeEnded) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 40 }}
        transition={{ ease: [0, 1, 1, 1] }}
        ref={ref}
        className={`relative z-40 w-full ${hideGradient ? 'bg-white' : 'bg-gradient-to-b from-[#5B2E4F] to-[#150E14]'}`}
      >
        <div className="p-[50px] md:p-[100px]">
          <div className={`flex flex-col gap-[10px] self-stretch bg-white ${!verticalStack ? 'md:flex-row' : ''}`}>
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
              className={`justify-top flex w-full flex-col px-10 pt-10 [filter:revert] ${!verticalStack ? 'md:w-1/2' : 'text-center'} md:p-10`}
            >
              <div className={verticalStack ? 'text-2xl md:text-4xl lg:text-5xl' : 'text-2xl'} data-i18n>
                <strong>{t('No sweepstakes at the moment')}</strong>
              </div>
              <div className="mt-2" data-i18n>
                {t('Check back soon! There aren’t any sweepstakes running right now, but be sure you’re signed up for emails so you don’t miss the next one.')}
              </div>
              <Button
                ref={ref}
                href="/#signup"
                label="Sign up for emails"
                buttonType="link"
                styleOverrides={{
                  css: { backgroundColor: '#000' },
                  tailwind:
                    'hover:outline-xumoTeal hover:weight-400 outline outline-2 outline-offset-2 outline-transparent transition-colors self-center mt-4 w-full md:w-auto',
                }}
                icon={undefined}
              />
            </motion.div>
          </div>
        </div>
      </motion.section>
    );
  }
  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 40 }}
      transition={{ ease: [0, 1, 1, 1] }}
      ref={ref}
      className={`relative z-40 w-full ${hideGradient ? 'bg-white' : 'bg-gradient-to-b from-[#5B2E4F] to-[#150E14]'}`}
    >
      {/*@ts-ignore */}
      <style jsx global>{`
        form a:hover {
          color: #bb2e76;
        }
      `}</style>
      <div className="p-[50px] md:p-[100px]">
        <div className={`flex flex-col gap-[10px] self-stretch bg-white ${!verticalStack ? 'md:flex-row' : ''}`}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
            className={`justify-top flex w-full flex-col px-10 pt-10 [filter:revert] ${!verticalStack ? 'md:w-1/2' : 'text-center'} md:p-10`}
          >
            <div className={verticalStack ? 'text-2xl md:text-4xl lg:text-5xl' : 'text-2xl'} data-i18n>
              <strong>{t(headingBold ?? 'Enter your info for a chance to win')}</strong>
            </div>
            <div className="mt-2" data-i18n>
              {t(body ?? '')}
            </div>
          </motion.div>
          <XumoForm
            formFields={[
              { name: 'first_name', type: InputType.Text, placeholder: 'First Name', width: 'half' },
              { name: 'last_name', type: InputType.Text, placeholder: 'Last Name', width: 'half' },
              { name: 'email', type: InputType.Email, placeholder: 'Email', width: 'full' },
              /* { name: 'phone', type: InputType.Tel, placeholder: 'Phone Number', width: 'half' }, */
              {
                name: 'legal',
                type: InputType.Checkbox,
                placeholder:
                  'I verify that I am at least eighteen (18) years of age and the age of majority in my state of residence (nineteen (19) in AL and NE, and twenty-one (21) in MS) and a legal United States resident residing in the fifty (50) United States or Washington, D.C. as of date of entry, and have had the opportunity to review and agree to the Official Rules and Sponsor’s Privacy Policy prior to entry. I also agree to be contacted by phone, e-mail or push notification for purposes of Sweepstakes administration if necessary.​',
                width: 'full',
              },
              {
                name: 'marketing',
                type: InputType.Checkbox,
                placeholder:
                  'I would like Xumo to send me automated marketing or sales emails or push notifications. I understand consent is not required to purchase Xumo products or services. Privacy Policy available at <a href="/privacy">Xumo.com/privacy</a>.​',
                width: 'full',
                customValidation: yup.string().notRequired(),
              },
            ]}
            CTA={{ label: ctaLabel ?? 'Enter', href: '#' }}
            formAction={{
              submissionEndpoint: '/api/sweepstake?sweekstakeId=' + slugifiedPath + '&startDate=' + startDate + '&endDate=' + endDate,
              successTitle: successTitle ?? 'Thanks for entering',
              successMessage: successMessage ?? 'You’re now entered for a chance to win a Xumo TV!',
              errorMessage: errorMessage ?? 'Unable to enter',
            }}
            styleOverrides={{
              form: `justify-top items-center w-full ${!verticalStack ? 'md:w-1/2' : ''} p-10 pt-5`,
              formField: `focus:border-b-xumoBerry focus:border-opacity-[100%] rounded-none mt-5 p-4 placeholder:text-black outline-1`,
              hideLabel: true,
              CTA: {
                general: `border-0 text-white px-6 mt-6 py-2 w-min`,
                disabledColor: 'bg-xumoCharcoal',
                enabledColor: 'bg-xumoBerry',
                alignment: `${verticalStack ? 'self-center' : 'self-start'} mt-4 w-full md:w-auto`,
              },
            }}
          />
        </div>
      </div>
    </motion.section>
  );
};
